import { Portal } from '@radix-ui/react-tooltip';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { useRef, useState } from 'react';

import anticipateDetail from '@/utils/anticipateRender/detail';
import { markdownToComponent } from '@/utils/convertMarkdown';
import formatDate from '@/utils/format/formatDate';
import formatName from '@/utils/format/formatName';
import formatURL from '@/utils/format/formatURL';
import getCurrentIds from '@/utils/getCurrentIds';
import getIcon from '@/utils/getIcon';
import getSource, { getSourceEventHandler } from '@/utils/getSource';
import { cn } from '@/utils/lib/utils';
import { Braces, ChevronDown, ChevronUp, RotateCw } from '@/utils/lucide';
import { ExternalLink } from '@/utils/lucide';
import { z_classes } from '@/utils/z';

import { EvidenceExplanation, ResearchSource } from '@/types/Evidence';

import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import SupportingQuoteComponent from './SupportingQuote';

// Helper function to extract hostname from URL
const getHostname = (url: string) =>
{
	try
	{
		const hostname = new URL(url).hostname;
		return hostname.replace('www.', '');
	}
	catch (e)
	{
		return url;
	}
};

// Source item component with favicon handling
const SourceItem = ({ source }: { source: ResearchSource }) =>
{
	const [faviconError, setFaviconError] = useState(false);
	const Icon = getIcon(source.source_type);
	const isWebpage = source.source_type === 'webpage';
	const hostname = isWebpage && source.url ? getHostname(source.url) : '';

	return (
		<div
			className='group relative flex flex-row items-center bg-gray-50 border gap-1 p-1 rounded-md cursor-pointer hover:bg-gray-100 active:bg-gray-200'
			onClick={(e: any) => getSource(source)}
			// onClick={(e: any) => getSourceEventHandler(e, source)}
		>
			{isWebpage && source.url && !faviconError ? (
				<div className='flex items-center justify-center min-w-4 h-4'>
					<img src={`https://www.google.com/s2/favicons?domain=${source.url}`} alt='' className='w-4 h-4' onError={() => setFaviconError(true)} />
				</div>
			) : (
				<Icon className='text-icon-sm text-gray-700 min-w-4' />
			)}
			<p className='text-start text-sm font-semibold pr-2 text-nowrap overflow-hidden text-ellipsis max-w-[40ch]'>
				{isWebpage && hostname ? hostname : source.title.trim()}
				<div className='opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute right-0 top-0 bg-gradient-to-r from-transparent to-gray-50 via-gray-50 min-h-8 w-16 z-30 via-60% group-hover:to-gray-100 group-hover:via-gray-100'></div>
			</p>
			<p className='absolute right-2 top-1 w-4 h-4 z-40'>
				<ExternalLink className='text-icon-sm text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200' />
			</p>
		</div>
	);
};

const ResearchLogPanel = ({ evidence, onRetry, embedded }: { evidence: EvidenceExplanation; onRetry?: () => void; embedded?: boolean }) =>
{
	const [isOpen, setIsOpen] = useState(true);
	const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});

	let textEvidenceClass = 'text-gray-500';
	let bgEvidenceClass = 'bg-gray-500';

	if (evidence.evidence_rating === 'No Evidence' || evidence.evidence_rating === 'Minimal Evidence')
	{
		textEvidenceClass = 'text-maven-error-400';
		bgEvidenceClass = 'bg-maven-error-100';
	}
	else if (evidence.evidence_rating === 'Indirect Evidence')
	{
		textEvidenceClass = 'text-maven-indicator-500';
		bgEvidenceClass = 'bg-maven-indicator-100';
	}
	else if (evidence.evidence_rating === 'Direct Evidence')
	{
		textEvidenceClass = 'text-maven-primary-600';
		bgEvidenceClass = 'bg-maven-primary-100';
	}

	const toggleSourceExpansion = (stepIndex: number) =>
	{
		setExpandedSteps(prev => ({
			...prev,
			[stepIndex]: !prev[stepIndex]
		}));
	};

	const isInternal = useFeatureFlagEnabled('internal');

	return (
		<div className='flex flex-col bg-white'>
			<div className='flex flex-row items-center justify-between p-2 bg-gray-50 border-b'>
				<p className={cn('text-lg font-bold pl-2', textEvidenceClass)}>{evidence.evidence_rating}</p>
				<div className='flex flex-row gap-0 items-center'>
					{isInternal && evidence.trace_url && (
						<a
							href={evidence.trace_url}
							target='_blank'
							rel='noopener noreferrer'
							className='hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-50 active:bg-gray-200  dark:active:bg-gray-700 dark:active:text-gray-50 text-xs font-semibold text-gray-400 hover:border p-2 hover:px-[7px] hover:text-gray-700 active:text-gray-900 w-8 h-8 flex items-center justify-center text-icon rounded-md'
						>
							<Braces className='text-icon-sm' />
						</a>
					)}
					{!embedded && (
						<Tooltip delayDuration={300}>
							<TooltipTrigger asChild>
								<Button
									variant={'ghost'}
									className='text-xs font-semibold text-gray-400 hover:border h-8 p-2 hover:px-[7px] hover:text-gray-700 active:text-gray-900'
									onClick={e =>
									{
										socket.emit('take_action', {
											action_type: 'evidence_detail',
											id: evidence.id,
											session_id: getCurrentIds().session_id
										});
										anticipateDetail();
									}}
								>
									<ExternalLink className='text-icon-sm' />
									<span className='hidden'>Retry</span>
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p className=''>Open Evidence Detail in a new tab</p>
							</TooltipContent>
						</Tooltip>
					)}
					{onRetry && (
						<Tooltip delayDuration={300}>
							<TooltipTrigger asChild>
								<Button
									variant={'ghost'}
									className='text-xs font-semibold text-gray-400 hover:border h-8 p-2 hover:px-[7px] hover:text-gray-700 active:text-gray-900'
									onClick={onRetry}
								>
									<RotateCw className='text-icon-sm' />
									<span className='hidden'>Retry</span>
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p className=''>Have AI generate a new value for this cell</p>
							</TooltipContent>
						</Tooltip>
					)}
				</div>
			</div>

			<p className='p-2 pl-4 text-sm font-medium'>{evidence.evidence_explanation}</p>

			{evidence.supporting_quotes && evidence.supporting_quotes.length > 0 && (
				<div className='flex flex-col p-2 gap-2'>
					<p className='text-base font-medium pl-2'>Key Evidence</p>
					{evidence.supporting_quotes.map((quote, index) => (
						<SupportingQuoteComponent key={index} quote={quote} index={index} />
					))}
				</div>
			)}

			{evidence.research_steps && evidence.research_steps.length > 0 && (
				<div className='mt-2'>
					<Collapsible open={isOpen} onOpenChange={setIsOpen}>
						<CollapsibleTrigger asChild>
							<Button
								variant='ghost'
								className='flex items-center justify-between w-full py-6 text-base font-medium border-t border-b rounded-none'
							>
								<span>Research Log</span>
								{isOpen ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
							</Button>
						</CollapsibleTrigger>
						<CollapsibleContent>
							<div className='relative ml-4 pl-4 pr-4 border-l border-gray-200'>
								{evidence.research_steps.map((step, index) => (
									<div key={index} className='py-3 relative'>
										{/* Step dot indicator */}
										<div className='absolute w-4 h-4 rounded-full bg-maven-primary-500/30 -left-[24.75px] top-[14px]'></div>
										<div className='absolute w-2 h-2 rounded-full bg-maven-primary-500 -left-[20.75px] top-[18px]'></div>

										{/* Step content */}
										<div className='text-sm text-gray-700 font-medium'>{step.text}</div>

										{/* Sources list */}
										{step.sources && step.sources.length > 0 && (
											<div className='mt-2 flex flex-col gap-2'>
												<div className='grid grid-cols-2 gap-2 @xl:grid-cols-3'>
													{step.sources.slice(0, expandedSteps[index] ? undefined : 4).map((source, sourceIndex) =>
													{
														if (!source) return null;

														return (
															<Tooltip delayDuration={300} key={sourceIndex}>
																<TooltipTrigger>
																	<SourceItem source={source} />
																</TooltipTrigger>
																<Portal>
																	<TooltipContent className={cn('max-w-[400px]', z_classes.overlay[2])} side='top'>
																		<div className='flex flex-col gap-1 p-2'>
																			<p className='text-gray-900 font-semibold'>{source.title}</p>
																			<div className='flex flex-row gap-2 text-sm'>
																				{source.source_type === 'webpage' && source.url ? (
																					<>
																						<span className='text-gray-500'>{formatURL(source.url)}</span>
																					</>
																				) : (
																					<span className='text-gray-500'>{formatName(source.source_type)}</span>
																				)}

																				{source.publish_date && (
																					<>
																						<span className='text-gray-400'>•</span>
																						<span className='text-gray-500'>{formatDate(source.publish_date)}</span>
																					</>
																				)}
																			</div>
																			{source.description && (
																				<div className='text-sm text-gray-700 mt-1 max-h-[80px] overflow-hidden text-ellipsis'>
																					{markdownToComponent(source.description)}
																				</div>
																			)}
																		</div>
																	</TooltipContent>
																</Portal>
															</Tooltip>
														);
													})}
													{step.sources.length > 4 && (
														<Button
															variant='outline'
															size='sm'
															className='w-fit text-sm h-8'
															onClick={() => toggleSourceExpansion(index)}
														>
															{expandedSteps[index] ? 'Show less' : `Show ${step.sources.length - 4} more`}
														</Button>
													)}
												</div>
											</div>
										)}
									</div>
								))}
							</div>
						</CollapsibleContent>
					</Collapsible>
				</div>
			)}
		</div>
	);
};

export default ResearchLogPanel;
